"use client";

import type { CarouselApi } from "@/components/rac/carousel";
import { Carousel, CarouselContent, CarouselItem } from "@/components/rac/carousel";
import { cn } from "@/utils/cn";
import { useRouter } from "next/navigation";
import { Key, ReactNode, useCallback, useEffect, useRef, useState, useId } from "react";

export interface CarouselTabItem {
	id: string;
	label: string | ReactNode;
	icon?: ReactNode;
	content?: ReactNode | (() => ReactNode);
}

interface CarouselTabsProps {
	tabs: CarouselTabItem[];
	defaultSelectedKey?: string;
	selectedKey?: string;
	className?: string;
	variant?: "default" | "pills" | "underline" | "cards";
	size?: "sm" | "md" | "lg";
	orientation?: "horizontal" | "vertical";
	onSelectionChange?: (key: Key) => void;
	urlQueryParam?: string;
	usePushState?: boolean;
	itemClassName?: string;
}

export function CarouselTabs({
	tabs,
	defaultSelectedKey,
	selectedKey,
	className,
	variant = "pills",
	size = "md",
	orientation = "horizontal",
	onSelectionChange,
	urlQueryParam,
	usePushState = false,
	itemClassName,
}: CarouselTabsProps) {
	const router = useRouter();
	const isVertical = orientation === "vertical";
	const [api, setApi] = useState<CarouselApi>();
	const [sliderStyle, setSliderStyle] = useState({});
	const tabListRef = useRef<HTMLDivElement>(null);
	const instanceId = useId();

	// Helper function to update URL
	const updateUrl = useCallback(
		(tabId: string) => {
			if (!urlQueryParam || typeof window === "undefined") return;

			const params = new URLSearchParams(window.location.search);
			params.set(urlQueryParam, tabId);
			const newUrl = `?${params.toString()}`;

			if (usePushState) {
				// Use pushState to update URL without navigation
				window.history.pushState({}, "", `${window.location.pathname}${newUrl}`);
			} else {
				// Use Next.js router for navigation
				router.push(newUrl, { scroll: false });
			}
		},
		[urlQueryParam, usePushState, router],
	);

	// Initialize activeTabId from URL if urlQueryParam is provided
	const getInitialTabId = () => {
		if (urlQueryParam && typeof window !== "undefined") {
			const params = new URLSearchParams(window.location.search);
			const tabFromUrl = params.get(urlQueryParam);
			if (tabs.find(tab => tab.id === tabFromUrl)) {
				return tabFromUrl;
			}
		}
		return selectedKey || defaultSelectedKey || tabs[0]?.id;
	};

	const [activeTabId, setActiveTabId] = useState(getInitialTabId);

	// Update slider position for pills variant with optimized batching
	const updateSliderPosition = useCallback(() => {
		if (variant !== "pills" || !tabListRef.current) return;

		const activeButton = tabListRef.current.querySelector(`[data-tab-id="${activeTabId}"]`) as HTMLElement;
		if (!activeButton) return;

		// Use requestAnimationFrame to batch DOM reads and writes
		requestAnimationFrame(() => {
			// Check if elements are still valid before measuring
			if (!activeButton.isConnected || !tabListRef.current) return;

			// Batch all DOM reads together to minimize reflows
			const rect = activeButton.getBoundingClientRect();
			const parentRect = tabListRef.current.getBoundingClientRect();

			// Only update if measurements are valid
			if (rect.width === 0 || rect.height === 0) return;

			// Batch DOM writes after all reads are complete
			const newStyle = {
				position: "absolute" as const,
				transition: "all 300ms ease-out",
				height: `${rect.height}px`,
				width: `${rect.width}px`,
				left: `${rect.left - parentRect.left}px`,
				top: `${rect.top - parentRect.top}px`,
			};

			setSliderStyle(newStyle);
		});
	}, [activeTabId, variant]);

	// Update slider position when tab changes or component mounts
	useEffect(() => {
		updateSliderPosition();
	}, [updateSliderPosition]);

	// Use ResizeObserver instead of resize event listener for better performance
	useEffect(() => {
		if (!tabListRef.current || variant !== "pills") return;

		const resizeObserver = new ResizeObserver(() => {
			// Debounce resize updates to prevent excessive reflows
			updateSliderPosition();
		});

		resizeObserver.observe(tabListRef.current);

		return () => {
			resizeObserver.disconnect();
		};
	}, [updateSliderPosition, variant]);

	// Sync with carousel API
	useEffect(() => {
		if (!api) return;

		const onSelect = () => {
			const selectedIndex = api.selectedScrollSnap();
			const selectedTab = tabs[selectedIndex];
			if (selectedTab && selectedTab.id !== activeTabId) {
				setActiveTabId(selectedTab.id);

				// Update URL if needed
				updateUrl(selectedTab.id);

				// Only call onSelectionChange if not using pushState (to avoid redundant handlers)
				onSelectionChange?.(selectedTab.id);
			}
		};

		api.on("select", onSelect);
		return () => {
			api.off("select", onSelect);
		};
	}, [api, activeTabId, updateUrl, onSelectionChange, tabs]);

	// Handle tab button clicks
	const handleTabClick = (tabId: string, index: number) => {
		// Navigate carousel to the selected tab
		api?.scrollTo(index);
	};

	const getTabListClasses = () => {
		const baseClasses = cn("flex w-full relative", isVertical ? "flex-col gap-1" : "flex-row");

		switch (variant) {
			case "pills":
				return cn(
					baseClasses,
					"bg-muted/50 rounded-2xl p-2 gap-2 border-none",
					isVertical ? "max-w-xs" : "justify-center max-w-full mx-auto overflow-x-auto scrollbar-hide",
				);
			case "underline":
				return cn(baseClasses, "border-b border-border gap-6", isVertical ? "border-b-0 border-r" : "");
			case "cards":
				return cn(baseClasses, "gap-4", isVertical ? "max-w-xs" : "justify-center");
			default:
				return cn(baseClasses, "gap-2", isVertical ? "max-w-xs" : "justify-center");
		}
	};

	const getTabClasses = (isActive: boolean) => {
		const sizeClasses = {
			sm: "px-3 py-2 text-sm",
			md: "px-4 sm:px-6 py-3 text-xs sm:text-sm",
			lg: "px-6 sm:px-8 py-4 text-sm sm:text-base",
		};

		const baseClasses = cn(
			"relative inline-flex items-center justify-center gap-2 font-medium transition-all duration-300 ease-out cursor-pointer outline-none z-10",
			"focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
			"disabled:pointer-events-none disabled:opacity-50",
			sizeClasses[size],
			isVertical ? "w-full justify-start" : "whitespace-nowrap min-w-0 flex-shrink-0",
		);

		switch (variant) {
			case "pills":
				return cn(baseClasses, "rounded-xl transition-colors duration-300 text-muted-foreground hover:text-foreground");
			case "underline":
				return cn(
					baseClasses,
					"after:absolute after:transition-opacity",
					isVertical
						? "after:right-0 after:top-0 after:h-full after:w-0.5 after:bg-foreground"
						: "after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-foreground",
					isActive
						? "text-foreground after:opacity-100"
						: "text-muted-foreground hover:text-foreground/80 after:opacity-0",
				);
			case "cards":
				return cn(
					baseClasses,
					"rounded-lg border bg-background shadow-sm hover:shadow-md hover:border-primary/20",
					isActive ? "border-primary bg-primary/5 text-primary-700 shadow-md" : "text-muted-foreground",
				);
			default:
				return cn(
					baseClasses,
					"rounded-md border bg-background hover:bg-muted/50 hover:border-primary/20",
					isActive ? "bg-primary text-primary-foreground border-primary shadow-sm" : "text-muted-foreground",
				);
		}
	};

	const activeTabIndex = tabs.findIndex(tab => tab.id === activeTabId);

	return (
		<div className={cn("w-full", className)}>
			{/* Tab List - Traditional tab navigation */}
			{tabs.length > 1 && (
				<div className={cn("flex", isVertical ? "flex-col" : "justify-center mb-2 sm:mb-4")}>
					<div
						ref={tabListRef}
						className={getTabListClasses()}
						role="tablist"
						aria-orientation={isVertical ? "vertical" : "horizontal"}
					>
						{/* Sliding background for pills variant */}
						{variant === "pills" && (
							<div
								className="absolute bg-background shadow-md rounded-xl pointer-events-none z-0"
								style={sliderStyle}
							/>
						)}

						{tabs.map((tab, index) => {
							const isActive = tab.id === activeTabId;

							return (
								<button
									key={tab.id}
									id={`${instanceId}-tab-${tab.id}`}
									data-tab-id={tab.id}
									onClick={() => handleTabClick(tab.id, index)}
									className={getTabClasses(isActive)}
									type="button"
									role="tab"
									aria-selected={isActive}
									aria-controls={`${instanceId}-panel-${tab.id}`}
								>
									{tab.icon && <span className="flex-shrink-0">{tab.icon}</span>}
									<span>{tab.label}</span>
								</button>
							);
						})}
					</div>
				</div>
			)}

			{/* Carousel Content - Swipeable content */}
			<Carousel
				setApi={setApi}
				className="w-full"
				opts={{
					align: "start",
					loop: false,
					skipSnaps: false,
					startIndex: activeTabIndex >= 0 ? activeTabIndex : 0,
				}}
				style={{ overflow: "visible" }} // To allow content to be fully visible
			>
				<CarouselContent className="h-auto">
					{tabs.map(tab => {
						const content = typeof tab.content === "function" ? tab.content() : tab.content;
						return (
							<CarouselItem key={tab.id} className="w-full">
								<div
									role="tabpanel"
									id={`${instanceId}-panel-${tab.id}`}
									aria-labelledby={`${instanceId}-tab-${tab.id}`}
									className={cn(
										"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[800px]",
										variant === "underline" ? "pt-8" : "pt-0",
										itemClassName,
									)}
								>
									{content}
								</div>
							</CarouselItem>
						);
					})}
				</CarouselContent>
			</Carousel>
		</div>
	);
}
